import { RefObject, useState } from "react";
import { FieldValues, useFieldArray, useFormContext } from "react-hook-form";
import ReferenceType from "$types/ReferenceType";
import { Reception } from "$types";
import { ProductReferenceList } from "$types/ProductReferenceList";

import ReferenceBlock from "../ReferencingBlock";
import Header from "../Header/Header";
import ProductReferenceListComponent from "./ProductReferenceList";
import { VFSOLUTIONS } from "$utils/constants";

type Props = {
  addToEditedBlocks: (item: { id: string; type: string }) => void;
  companyId: string;
  warehouse: string;
  editedBlocks: { id: string; type: string }[];
  handleOnCancelEdit: (_id: string, index: number, type: ReferenceType) => void;
  hasReferencingCategory: boolean;
  index: number;
  isFocused: boolean;
  isReadOnly: boolean;
  onDelete: (index: number) => void;
  setIndexToDelete(index: number);
  onReferenceTypeChange: () => void;
  productRefList: ProductReferenceList;
  reception: Reception;
  scrollToRef?: RefObject<HTMLDivElement>;
  downloadOrderReference?: () => void;
  createOrderReference?: () => void;
  openDeleteModal: boolean;
  confirmedDeletions: boolean;
};

function ProductReferenceListWrapper({
  addToEditedBlocks,
  companyId,
  warehouse,
  editedBlocks,
  handleOnCancelEdit,
  hasReferencingCategory,
  index,
  isFocused,
  isReadOnly,
  onDelete,
  setIndexToDelete,
  onReferenceTypeChange,
  productRefList,
  reception,
  scrollToRef,
  openDeleteModal,
  downloadOrderReference,
  createOrderReference,
  confirmedDeletions,
}: Props) {
  const hasOneOldProduct = productRefList?.productReferences.some((p) => p.id);
  const isNew = productRefList?.productReferences.every((p) => !p.id);

  const localIsReadOnly =
    !isNew &&
    !editedBlocks.find((obj) => obj.id === String(productRefList?.id)) &&
    !isFocused;

  const [deletedItems, setDeletedItems] = useState<number[]>([]);

  const { control } = useFormContext<FieldValues>();
  const { fields: productRefFields } = useFieldArray({
    control,
    name: `productReferenceLists.${index}.productReferences`,
    keyName: "_id",
  });

  // if we deleted all the product refs, no need to render the component
  if (
    productRefFields.every((field: any) =>
      deletedItems.some((id) => id === field.id)
    ) &&
    productRefFields.length > 0
  ) {
    return null;
  }

  return (
    <div ref={isFocused ? scrollToRef : null} key={productRefList?.id}>
      <ReferenceBlock isReadOnly={localIsReadOnly}>
        <Header
          editedBlocks={editedBlocks}
          addToEditedBlocks={addToEditedBlocks}
          allowToDelete={
            !isReadOnly ||
            isNew ||
            !!editedBlocks.find((obj) => obj.id === String(productRefList?.id))
          }
          allowToEdit={
            hasOneOldProduct || productRefList?.productReferences.length > 1
          }
          blockId={String(productRefList?.id)}
          companyId={companyId}
          hasReferencingCategory={hasReferencingCategory}
          isReadOnly={localIsReadOnly}
          nestIndex={index}
          setIndexToDelete={setIndexToDelete}
          onCancelEdit={handleOnCancelEdit}
          onDelete={onDelete}
          onReferenceTypeChange={onReferenceTypeChange}
          orders={
            productRefList?.productReferences?.find(
              (pr) => !!pr.productReference
            )?.orders || productRefList?.productReferences[0].orders
          }
          reception={reception}
          suborders={productRefList?.suborders}
          productReferences={productRefList.productReferences}
          value={ReferenceType.PRODUCT}
          downloadOrderReference={downloadOrderReference}
          createOrderReference={createOrderReference}
          showLinks={
            reception?.receptionReferences?.length > 0 &&
            warehouse?.indexOf(VFSOLUTIONS) !== -1
          }
        />
        <ProductReferenceListComponent
          productRefFields={productRefFields}
          companyId={reception.company}
          hasReferencingCategory={hasReferencingCategory}
          isReadOnly={localIsReadOnly}
          nestIndex={index}
          reception={reception}
          confirmedDeletions={confirmedDeletions}
          deletedItems={deletedItems}
          openDeleteModal={openDeleteModal}
          setDeletedItems={setDeletedItems}
        />
      </ReferenceBlock>
    </div>
  );
}

export default ProductReferenceListWrapper;
